import { RANKS, RANK_INDEX, type Rank } from "./constants";

export const GRID_SIZE = 13;
export const TOTAL_CELLS = GRID_SIZE * GRID_SIZE; // 169
export const TOTAL_COMBOS = 1326;

/**
 * Map a (row, col) coordinate to a canonical hand string.
 *  - row === col → pocket pair      e.g. (0,0) → "AA"
 *  - row <  col  → suited           e.g. (0,1) → "AKs"
 *  - row >  col  → offsuit          e.g. (1,0) → "AKo"
 */
export function cellToHand(row: number, col: number): string {
  if (row < 0 || row > 12 || col < 0 || col > 12) {
    throw new RangeError(`cell (${row}, ${col}) out of grid bounds`);
  }
  if (row === col) return RANKS[row]! + RANKS[col]!;
  if (col > row) return RANKS[row]! + RANKS[col]! + "s";
  return RANKS[col]! + RANKS[row]! + "o";
}

export function handToCell(hand: string): { row: number; col: number } {
  if (hand.length === 2) {
    const r = hand[0] as Rank;
    if (RANK_INDEX[r] === undefined || hand[0] !== hand[1]) {
      throw new Error(`invalid pair "${hand}"`);
    }
    const i = RANK_INDEX[r];
    return { row: i, col: i };
  }
  if (hand.length === 3) {
    const a = hand[0] as Rank;
    const b = hand[1] as Rank;
    const suit = hand[2];
    if (RANK_INDEX[a] === undefined || RANK_INDEX[b] === undefined) {
      throw new Error(`invalid hand "${hand}"`);
    }
    const ai = RANK_INDEX[a];
    const bi = RANK_INDEX[b];
    if (suit === "s") return { row: Math.min(ai, bi), col: Math.max(ai, bi) };
    if (suit === "o") return { row: Math.max(ai, bi), col: Math.min(ai, bi) };
  }
  throw new Error(`invalid hand "${hand}"`);
}

export function cellIndex(row: number, col: number): number {
  return row * GRID_SIZE + col;
}

export function rowColFromIndex(idx: number): { row: number; col: number } {
  if (idx < 0 || idx >= TOTAL_CELLS) {
    throw new RangeError(`cell index ${idx} out of range`);
  }
  return { row: Math.floor(idx / GRID_SIZE), col: idx % GRID_SIZE };
}

/** Combos for a cell — used for percentage math. */
export function combosFor(row: number, col: number): number {
  if (row === col) return 6; // pocket pair
  if (col > row) return 4; // suited
  return 12; // offsuit
}

/** Cell indices for pocket pairs (diagonal: row === col). */
export function pocketPairIndices(): number[] {
  const out: number[] = [];
  for (let i = 0; i < GRID_SIZE; i++) out.push(cellIndex(i, i));
  return out;
}

/** Cell indices for suited hands (upper triangle: col > row). */
export function suitedIndices(): number[] {
  const out: number[] = [];
  for (let r = 0; r < GRID_SIZE; r++)
    for (let c = r + 1; c < GRID_SIZE; c++) out.push(cellIndex(r, c));
  return out;
}

/** Cell indices for broadway hands (A/K/Q/J/T = ranks 0-4, all combos). */
export function broadwayIndices(): number[] {
  const out: number[] = [];
  for (let r = 0; r < 5; r++)
    for (let c = 0; c < 5; c++) out.push(cellIndex(r, c));
  return out;
}

/** All 169 cell indices. */
export function allIndices(): number[] {
  const out: number[] = [];
  for (let i = 0; i < TOTAL_CELLS; i++) out.push(i);
  return out;
}

/** Pre-computed 13x13 hand grid. Frozen. */
export const GRID_HAND_MAP: readonly (readonly string[])[] = (() => {
  const grid: string[][] = [];
  for (let r = 0; r < GRID_SIZE; r++) {
    const row: string[] = [];
    for (let c = 0; c < GRID_SIZE; c++) row.push(cellToHand(r, c));
    grid.push(row);
  }
  return Object.freeze(grid.map((r) => Object.freeze(r)));
})();
